import { useCallback, useEffect, useRef, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { ArrowLeft, Loader2, Plus, Send, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import {
  callDiagnosisService,
  type DiagnosisFeedbackRow,
  type DiagnosisMessageRow,
  type DiagnosisThreadSummary,
} from "@/services/callDiagnosisService";
import { DiagnosisMessage } from "./DiagnosisMessage";

export interface DiagnoseCall {
  id: string;
  roomName?: string | null;
  agentName?: string | null;
  traceId?: string | null;
  externalCallId?: string | null;
  organizationId?: string | null;
  callDate?: string | null;
  langfuseUrl?: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  call: DiagnoseCall;
  // fired after a run finishes, so the call list can refresh its count
  onRunComplete?: (callId: string) => void;
}

const FORBIDDEN = "Diagnose is only available to F22 Labs members.";
const POLL_MS = 2000;
const STALE_RUNNING_MS = 5 * 60_000;

const newMessage = (over: Partial<DiagnosisMessageRow>): DiagnosisMessageRow => ({
  id: `tmp-${Math.random().toString(36).slice(2)}`,
  thread_id: "",
  role: "assistant",
  text: "",
  steps: [],
  status: "running",
  error: null,
  input_tokens: 0,
  output_tokens: 0,
  cache_read_tokens: 0,
  cache_write_tokens: 0,
  cost_usd: 0,
  created_by: null,
  created_by_email: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  feedback: [],
  ...over,
});

const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true });

export function DiagnoseSheet({ open, onOpenChange, call, onRunComplete }: Props) {
  const [view, setView] = useState<"list" | "thread">("list");
  const [threads, setThreads] = useState<DiagnosisThreadSummary[]>([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<DiagnosisMessageRow[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<{ id: string; email: string | null } | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const loadThreads = useCallback(async () => {
    setThreadsLoading(true);
    try {
      setThreads(await callDiagnosisService.listThreads(call.id));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load diagnoses");
    } finally {
      setThreadsLoading(false);
    }
  }, [call.id]);

  // Fresh list view each time the sheet opens. Closing aborts the fetch only: the server keeps
  // running and the poll below picks the answer up on reopen.
  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      setStreaming(false);
      return;
    }
    setView("list");
    setThreadId(null);
    setMessages([]);
    setDraft("");
    setError(null);
    callDiagnosisService.getCurrentUser().then(setUser).catch(() => {});
    loadThreads();
  }, [open, call.id, loadThreads]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const openThread = async (id: string) => {
    setThreadId(id);
    setView("thread");
    setMessages([]);
    setError(null);
    setMessagesLoading(true);
    try {
      setMessages(await callDiagnosisService.getThreadMessages(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load the thread");
    } finally {
      setMessagesLoading(false);
    }
  };

  const newThread = () => {
    setThreadId(null);
    setMessages([]);
    setError(null);
    setView("thread");
  };

  const backToList = () => {
    if (streaming) return;
    setView("list");
    loadThreads();
  };

  // A row can be `running` with no stream here (sheet reopened, or another user started it).
  // Past STALE_RUNNING_MS the run died (same cut-off as diagnose_call): stop polling, allow a new question.
  const orphanRunning = messages.find(
    (m) =>
      m.role === "assistant" &&
      m.status === "running" &&
      !m.id.startsWith("tmp-") &&
      Date.now() - new Date(m.created_at).getTime() < STALE_RUNNING_MS,
  );
  const orphanId = !streaming ? orphanRunning?.id : undefined;

  useEffect(() => {
    if (!orphanId) return;
    const timer = setInterval(async () => {
      try {
        const fresh = await callDiagnosisService.getMessage(orphanId);
        if (!fresh) return;
        setMessages((prev) => prev.map((m) => (m.id === orphanId ? { ...m, ...fresh, feedback: m.feedback } : m)));
        if (fresh.status !== "running") onRunComplete?.(call.id);
      } catch {
        // transient; the next tick retries
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [orphanId, call.id, onRunComplete]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [messages, view]);

  const send = async () => {
    const query = draft.trim();
    if (!query || streaming || orphanRunning) return;
    setDraft("");
    setError(null);

    const userMsg = newMessage({ role: "user", text: query, status: "done", created_by_email: user?.email ?? null });
    let assistantId = newMessage({}).id;
    const assistantMsg = newMessage({ id: assistantId });
    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setStreaming(true);

    const patch = (fn: (m: DiagnosisMessageRow) => DiagnosisMessageRow) =>
      setMessages((prev) => prev.map((m) => (m.id === assistantId ? fn(m) : m)));

    const controller = new AbortController();
    abortRef.current = controller;

    await callDiagnosisService.runDiagnosis(
      { callLogId: call.id, threadId: threadId ?? undefined, query },
      {
        onStart: (e) => {
          setThreadId(e.thread_id);
          // Copy the placeholder ids first: React runs the updater later, after the reassignments
          // below, so reading userMsg.id / assistantId inside it would never match.
          const tmpUserId = userMsg.id;
          const tmpAssistantId = assistantId;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tmpUserId
                ? { ...m, id: e.user_message_id, thread_id: e.thread_id }
                : m.id === tmpAssistantId
                  ? { ...m, id: e.assistant_message_id, thread_id: e.thread_id }
                  : m,
            ),
          );
          userMsg.id = e.user_message_id;
          assistantId = e.assistant_message_id;
        },
        onStep: (s) => patch((m) => ({ ...m, steps: [...m.steps, s] })),
        onText: (d) => patch((m) => ({ ...m, text: (m.text ?? "") + d })),
        onDone: (d) =>
          patch((m) => ({
            ...m,
            id: d.message_id,
            text: d.text,
            status: "done",
            input_tokens: d.input_tokens,
            output_tokens: d.output_tokens,
            cache_read_tokens: d.cache_read_tokens,
            cache_write_tokens: d.cache_write_tokens,
            cost_usd: d.cost_usd,
          })),
        onError: (message, status) => {
          const text = status === 403 ? FORBIDDEN : message;
          if (status === undefined) {
            patch((m) => ({ ...m, status: "error", error: text }));
          } else {
            // rejected before streaming: drop the placeholder rows and keep the question to retry
            setMessages((prev) => prev.filter((m) => m.id !== assistantId && m.id !== userMsg.id));
            setDraft(query);
            setError(`${text}${status === 403 ? "" : " Try again."}`);
          }
        },
      },
      controller.signal,
    );

    if (abortRef.current === controller) {
      abortRef.current = null;
      setStreaming(false);
      onRunComplete?.(call.id);
    }
  };

  const setFeedback = (messageId: string, rows: DiagnosisFeedbackRow[]) =>
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, feedback: rows } : m)));

  const issueContext = {
    callLogId: call.id,
    roomName: call.roomName,
    organizationId: call.organizationId,
    assistantName: call.agentName,
    callDate: call.callDate,
    langfuseUrl: call.langfuseUrl,
  };

  const busy = streaming || !!orphanRunning;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="z-[60] w-full gap-0 p-0 sm:max-w-[520px]">
        <SheetHeader className="border-b p-4 pr-10">
          <div className="flex items-center gap-2">
            {view === "thread" && (
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={backToList} disabled={streaming} aria-label="Back to diagnoses">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
            <SheetTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="h-4 w-4" />
              Diagnose call
            </SheetTitle>
          </div>
          <SheetDescription className="truncate font-mono text-xs">
            {call.roomName || call.id}
            {call.agentName ? ` · ${call.agentName}` : ""}
          </SheetDescription>
        </SheetHeader>

        {view === "list" ? (
          <div className="flex-1 space-y-3 overflow-y-auto p-4">
            <Button onClick={newThread} className="w-full">
              <Plus className="mr-1 h-4 w-4" />
              New diagnosis
            </Button>
            {error && <p className="text-sm text-destructive">{error}</p>}
            {threadsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : threads.length === 0 ? (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                Diagnose reads this call's Langfuse trace and server logs, then explains what went wrong and the likely
                fix. Ask a question like "Why did the agent stop talking after 40s?". Everyone on the team sees the
                same threads and can mark answers correct or wrong.
              </div>
            ) : (
              <ul className="space-y-2">
                {threads.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => openThread(t.id)}
                      className="w-full rounded-lg border p-3 text-left transition-colors hover:bg-muted"
                    >
                      <div className="line-clamp-2 text-sm font-medium">{t.title}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                        <span>{t.created_by_email ?? "unknown"}</span>
                        <span>· {ago(t.created_at)}</span>
                        <span>
                          ·{" "}
                          {t.lastStatus === "running"
                            ? "running"
                            : t.lastStatus === "error"
                              ? "failed"
                              : t.lastStatus === "done"
                                ? "answered"
                                : "no answer"}
                        </span>
                        {(t.correct > 0 || t.wrong > 0) && (
                          <span>
                            · <span className="text-good">✓ {t.correct}</span>{" "}
                            <span className="text-danger">✗ {t.wrong}</span>
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <>
            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              {messagesLoading && (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!messagesLoading && messages.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Ask what to look into. The answer starts with a verdict, with timeline and evidence below it.
                </p>
              )}
              {messages.map((m, i) =>
                m.role === "user" ? (
                  <div key={m.id} className="ml-8 rounded-lg bg-muted p-3">
                    <div className="mb-1 text-xs text-muted-foreground">{m.created_by_email ?? "you"}</div>
                    <p className="whitespace-pre-wrap break-words text-sm">{m.text}</p>
                  </div>
                ) : (
                  <DiagnosisMessage
                    key={m.id}
                    message={m}
                    question={messages[i - 1]?.role === "user" ? (messages[i - 1].text ?? "") : ""}
                    issueContext={issueContext}
                    currentUserId={user?.id ?? null}
                    onFeedbackChange={setFeedback}
                  />
                ),
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div ref={bottomRef} />
            </div>

            <div className="space-y-2 border-t p-4">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="What should I look into? e.g. Why did the agent stop talking after 40s?"
                rows={3}
                maxLength={4000}
                disabled={busy}
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Cmd/Ctrl+Enter to send</span>
                <Button size="sm" onClick={send} disabled={busy || !draft.trim()}>
                  {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                  {busy ? "Running" : "Send"}
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
